/** Content → Blog posts list. */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Checkbox, Empty, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { StatusTag, fmtDate, listOf, sitePath, toastErr, totalOf, useSite } from './common';
import { Thumb } from './ShopCommon';
import { ExportButton, ImportButton } from './blog/ImportExport';

const { Text } = Typography;

const Blog = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { api, siteId } = useSite();
    const [f, setF] = useState({ q: '', status: '', tag: '', page: 1, per_page: 25 });
    const [q, setQ] = useState('');
    const [data, setData] = useState({ items: [], total: 0, tags: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let live = true;
        setLoading(true);
        api.get('/posts', Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '')))
            .then((d) => live && setData({ items: listOf(d), total: totalOf(d), tags: listOf(d?.tags) }))
            .catch((e) => { if (live) { if (e.status !== 404) toastErr(e); setData({ items: [], total: 0, tags: [] }); } })
            .finally(() => live && setLoading(false));
        return () => { live = false; };
    }, [api, f]);
    const [cats, setCats] = useState([]);
    useEffect(() => { api.get('/post-terms', { kind: 'category' }, QUIET).then((d) => setCats(listOf(d))).catch(() => {}); }, [api]);
    const create = () => navigate(sitePath(siteId, 'blog/new'));

    return (
        <Card title={t('Blog posts')} extra={<Space><ImportButton onDone={() => setF((x) => ({ ...x }))} /><ExportButton /><Button type="primary" icon={<PlusOutlined />} onClick={create}>{t('Write post')}</Button></Space>}>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search allowClear placeholder={t('Search posts')} value={q} onChange={(e) => setQ(e.target.value)} onSearch={(v) => setF((x) => ({ ...x, q: v, page: 1 }))} style={{ width: 240 }} />
                <Select allowClear placeholder={t('Status')} style={{ width: 140 }} value={f.status || undefined} onChange={(v) => setF((x) => ({ ...x, status: v || '', page: 1 }))}
                    options={[{ value: 'published', label: t('Published') }, { value: 'scheduled', label: t('Scheduled') }, { value: 'draft', label: t('Draft') }]} />
                {cats.length > 0 && <Select allowClear placeholder={t('Category')} style={{ width: 160 }} value={f.category_id || undefined} onChange={(v) => setF((x) => ({ ...x, category_id: v || '', page: 1 }))} options={cats.map((c) => ({ value: c.id, label: c.name }))} />}
                <Checkbox checked={!!f.featured} onChange={(e) => setF((x) => ({ ...x, featured: e.target.checked ? 1 : '', page: 1 }))}>{t('Featured')}</Checkbox>
                {data.tags.length > 0 && <Select allowClear placeholder={t('Tag')} style={{ width: 160 }} value={f.tag || undefined} onChange={(v) => setF((x) => ({ ...x, tag: v || '', page: 1 }))} options={data.tags.map((x) => ({ value: x, label: x }))} />}
            </Space>
            <Table rowKey="id" loading={loading} dataSource={data.items} scroll={{ x: 680 }}
                onRow={(p) => ({ onClick: () => navigate(sitePath(siteId, `blog/${p.id}`)), style: { cursor: 'pointer' } })}
                pagination={{ current: f.page, pageSize: f.per_page, total: data.total, hideOnSinglePage: true, onChange: (page, per) => setF((x) => ({ ...x, page, per_page: per })) }}
                columns={[
                    { title: t('Title'), key: 't', render: (_, p) => <Space><Thumb src={p.cover?.src} /><div><a>{p.title}</a>{p.author && <div><Text type="secondary" style={{ fontSize: 12 }}>{p.author}</Text></div>}</div></Space> },
                    { title: t('Status'), dataIndex: 'status', width: 190, render: (v, p) => (p.status_label === 'scheduled' ? <Tag color="blue">{t('Scheduled for {{date}}', { date: fmtDate(p.published_at) })}</Tag> : <StatusTag value={v} />) },
                    { title: t('Tags'), dataIndex: 'tags', responsive: ['md'], render: (v) => listOf(v).map((x) => <Tag key={x}>{x}</Tag>) },
                    { title: t('Published'), dataIndex: 'published_at', width: 170, render: (v, p) => (p.status === 'published' ? fmtDate(v) : '-') },
                ]}
                locale={{ emptyText: <Empty description={t('No posts yet')}><Button type="primary" icon={<PlusOutlined />} onClick={create}>{t('Write post')}</Button></Empty> }} />
        </Card>
    );
};

export default Blog;
